/*****************************************************************************
 *   © Copyright 2021   Quantitative Software Management, Inc.               *
 *              --  All rights reserved.  --                                 *
 *   Unpublished -- rights reserved under the Copyright Laws                 *
 *   of the United States.                                                   *
 *                                                                           *
 *   This computer software is protected by trade secret, copyright law,     *
 *   and international treaties and contains proprietary and confidential    *
 *   trade secrets of Quantitative Software Management, Inc.  Unauthorized   *
 *   use, reproduction, or distribution of this program or any portion of    *
 *   it will result in severe civil or criminal penalties and will be        *
 *   prosecuted to the full extent of the law.                               *
 *                                                                           *
 *   Use, duplication, or disclosure by the Government is subject to         *
 *   restrictions as set forth in                                            *
 *      (i) a license agreement incorporated in the contract,                *
 *      (ii) the definition of "restricted rights" in paragraph (c)(1)(ii)   *
 *           of DFARS 252.227-7013 (1988), and                               *
 *      (iii) the definition of "restricted rights" in paragraph (a) of      *
 *            FAR 52.227-14, Alt III (Jun 1987) or                           *
 *      (iv) FAR 52.227-19 (Jun 1987), as applicable.                        *
 *   For purposes of the FAR, this software shall be deemed to               *
 *   be "unpublished" and licensed with disclosure prohibitions.             *
 *   Contractor/Manufacturer is   Quantitative Software Management, Inc.     *
 *                                                                           *
 *****************************************************************************/

import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Injector, NgZone } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import * as StackTrace from 'stacktrace-js';
import { AppConstants } from '../app.constants';
import { QsmServerError } from '../Models/QsmServerError';
import { NotifierComponent } from '../notifier/notifier.component';
import { BaseUtilityService } from './BaseUtility.Service';

@Injectable({
  providedIn: 'root'
})

export class ErrorService {
  private previousUrl: string = undefined;
  private currentUrl: string = undefined;
  private alreadyOnTheErrorPage: boolean = false;

  private messageSource: Subject<any> = new Subject();
  public currentMessage: Observable<any> = this.messageSource.asObservable();
  public clientErrorObj: BehaviorSubject<any> = new BehaviorSubject(null);

  constructor(private router: Router,
    private http: HttpClient,
    private baseUtilityService: BaseUtilityService,
    private injector: Injector,
    @Inject(DOCUMENT) private document: any,
    private ngZone: NgZone) {
    this.currentUrl = this.router.url;
    router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
        this.alreadyOnTheErrorPage = this.currentUrl.startsWith('/error') && this.previousUrl.startsWith('/error') ;
      };
    });
  }

  //public observerCount(): string {
  //  return "messageSource: " + this.messageSource.observers.length + ", clientErrObj: " + this.clientErrorObj.observers.length;
  //}

  //public ClearMessage(): void {
  //  if (this.messageSource.getValue() !== null) {
  //    this.messageSource.next(null);
  //  }
  //}

  private SendMessage(subj: string): void {
    this.messageSource.next({
      subject: subj,
      isErrorServiceMsg: true
    });
  }

  public NavigateToAspx(aspxUrl: string): void {
    this.SendMessage(AppConstants.ERROR_NAVIAGATION_MSG);
    this.baseUtilityService.NavigateToAspx(aspxUrl);
  }

  public NavigateToLogin() {
    this.SendMessage(AppConstants.ERROR_NAVIAGATION_MSG);
    this.baseUtilityService.NavigateToLogin();
  }

  private stop401: boolean = false;
  
  public ErrorRedirect(theError: HttpErrorResponse, theErrorTitle?: string, theErrorRedirect: boolean = false) {
    if (!this.alreadyOnTheErrorPage) {
      // Send the error via queryParms
      let serverError = this.getQsmServerError(theError);
      if (serverError) {
        if (serverError.redirect) {
          //for 4.0, it must be an aspx redirect!
          if (serverError.isAspxRedirect) {
            this.NavigateToAspx(serverError.redirect)// send a message to the project dialog component to prevent 'unsaved changes' message
          }
          else {
            //throw exception?
          }
        }
        else if (serverError.errorId || theErrorRedirect) {
          let errorObject: NavigationExtras = {
            queryParams: {
              "errorCode": serverError.errorCode,
              "errorMessage": serverError.errorMessage,
              "errorId": serverError.errorId,
              "requestUrl": serverError.requestUrl
            }
          };
          this.SendMessage(AppConstants.ERROR_NAVIAGATION_MSG);
          this.router.navigate(['/error'], errorObject);
        }
        else {
          const theNotifier = this.injector.get(NotifierComponent);
          theNotifier.ShowWindowNotification(theErrorTitle, serverError.errorMessage);
        }
      }
      else {
        if (theError.status == 401) {
          if (this.Stop401TimeoutKey) {
            clearTimeout(this.Stop401TimeoutKey);
          }
          this.Stop401TimeoutKey = setTimeout(() => {
            this.Stop401TimeoutKey = null;

            if (window === top) {
              this.ngZone.run(() => {
                this.NavigateToLogin();
              });
            }
            else {
              top.postMessage({ subject: "navigateToLogin" }, "/");

            }
          }, this.Stop401Timeout);
        }
        else {
          let errorMessage = theError.status == 0 ? "Network connectivity lost, can't connect to the server." : theError.message;
          let errorObject: NavigationExtras = {
            queryParams: {
              "errorCode": theError.status,
              "errorMessage": errorMessage,
              "errorId": null
            }
          };
          this.ngZone.run(() => {
            this.SendMessage(AppConstants.ERROR_NAVIAGATION_MSG);
            this.router.navigate(['/error'], errorObject)
          });
        }
      }
    }
    else {
      //this.baseUtilityService.debuglog(1, "Error already handled:", collabErrorHandled, theError);
    }
  }

  public ClientMappedErrorList: string[] = [];
  public ClientErrorList: Error[] = [];
  public AboutToRoute: boolean = false;
  public TIMESTAMP: string = "Timestamp";
  public ClientErrorTimeoutKey = null;
  public ClientErrorTimeout = 100;
  public Stop401TimeoutKey = null
  public Stop401Timeout = 500;
  public UseClientErrorList: string = "UseClientErrorList";

  public ClientError(theError: Error, theErrorTitle?: string) {
    if (!this.alreadyOnTheErrorPage) {
    if (theError instanceof HttpErrorResponse) {
      this.ErrorRedirect(theError);
    }
    else {
      theError['url'] = window.location.href;
      theError['Timestamp'] = new Date();
      console.error(theError['Timestamp'] + (theErrorTitle ?? "Angular Client error:"), theError);
      if (this.ClientErrorTimeoutKey) {
        clearTimeout(this.ClientErrorTimeoutKey);
      }
      if (!this.AboutToRoute) {
        if (!(window.frameElement)) {
          this.ClientErrorList.push(theError);
        }
        else {
          top.postMessage({
            subject: "pushClientErrorList",
            error: theError,
            errMessage: theError.message,
            errName: theError.name,
            errStack: theError.stack,
            errTimestamp: theError['Timestamp'],
            errUrl: theError['url']
          }, "/");
        }
      }
      this.ClientErrorTimeoutKey = setTimeout(() => {
        this.ClientErrorTimeoutKey = null;
        this.AboutToRoute = true;
        let errorObject: NavigationExtras = {
          queryParams: {
            UseClientErrorList: true
          }
        };
        this.ngZone.run(() => {
          this.SendMessage(AppConstants.ERROR_NAVIAGATION_MSG);
          this.router.navigate(['/error'], errorObject);
        });
      }, this.ClientErrorTimeout);
    }
  }
  }

  public MapClientError(error: Error): void {   
    var callback = function (stackframes) {
      var stringifiedStack = stackframes.map(function (sf) {
        return "at " + sf.functionName + " " + sf.fileName + " " + sf.lineNumber + ":" + sf.columnNumber;
      }).join('\n');
      error['mapStack'] = stringifiedStack;
      this.CountError();
    };
    var errback = function (err) { console.log("errback: ", err.message); };
    StackTrace.fromError(error).then(callback.bind(this)).catch(errback);
  }
  public MappedErrorCount: number = 0;

  public CountError() {
    this.MappedErrorCount++;
    if ((this.MappedErrorCount === this.ClientErrorList.length) && !this.alreadyOnTheErrorPage) {
      this.clientErrorObj.next(this.ClientErrorList);      
    }
  }   

  public MapClientErrors() {    
    this.ClientErrorList.forEach(x => {
      this.MapClientError(x);
    });
  }


  public getPreviousUrl() {
    return this.previousUrl;
  }

  public getQsmServerError(errorResponse: HttpErrorResponse): QsmServerError | null {
    let serverError = null;
    try {
      serverError = errorResponse.error as QsmServerError;
      if (!serverError || !serverError.isQSMServerError) {
        serverError = null;
      }
    }
    catch (e) {
      serverError = null;
      this.ClientError(e, "getQsmServerError not QsmServerError");
    }
    return serverError;
  }

  public LogError(data) {
    return this.http.post('Error/LogError', data, this.baseUtilityService.JsonOptions()).pipe(map((x: string) => {
      return x;
    }));
  }
}

